import React, { useState } from 'react';
import { UpgradeType, type UpgradeDto } from '../../types/ship-types';
import './ShipCard.css';
import '../UpgradeBranch.css';
import InfoPopover from '../InfoPopover';
// Utils
import { formatNumber } from '../../utils/formatNumber';
import { debugLog } from '../../utils/debugLogger';

// Import icons
import miningIcon from '../../assets/skills/mining.png';
import enginesIcon from '../../assets/skills/engines.png';
import cargoIcon from '../../assets/skills/cargo.png';

// Helper to convert numbers to Roman numerals (1-10)
const toRoman = (num: number) => {
  const romans = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
  return romans[num - 1] || num;
};

interface ShipUpgradeDisplayProps {
  upgrades: UpgradeDto[];
  onUpgradeClick?: (upgrade: UpgradeDto) => void;
  disabled?: boolean;
  queuedUpgradeId?: string | null; // ID of the currently queued upgrade, if any
  playerOre?: number;
  baseMiningSpeed?: number; // Optional base mining speed for calculations
  baseWarpSpeed?: number; // Optional base warp speed for calculations
  baseCargoCapacity?: number; // Optional base cargo capacity for calculations
  upgradeTree?: any; // Add upgradeTree prop type as needed
}

const ShipUpgradeDisplay: React.FC<ShipUpgradeDisplayProps> = (props) => {
  const { upgrades, onUpgradeClick, disabled, queuedUpgradeId, playerOre } = props;
  const [popoverOpenUpgrade, setPopoverOpenUpgrade] = useState<UpgradeDto | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const anyQueued = Boolean(queuedUpgradeId);

  // Grey out info button if any upgrade is queued or all are not affordable
  const allCannotAfford = upgrades.every(upg => playerOre !== undefined && upg.cost > playerOre);  // Find all upgrades for a specific branch when popover is shown

  return (
    <div className="upgrade-branch-row" style={{ position: 'relative' }}>
      {/* Upgrades row */}
      {upgrades.map((upgrade) => {
        const cannotAfford = playerOre !== undefined && upgrade.cost > playerOre;
        const isQueued = queuedUpgradeId === upgrade.id;
        const isBuyable = !upgrade.isUnlocked && !cannotAfford && !disabled && !anyQueued;
        // Determine if this upgrade is at max level
        let allUpgrades = props.upgrades.filter(u => u.type === upgrade.type);
        if (props.upgradeTree && props.upgradeTree.branches) {
          const branch = props.upgradeTree.branches.find((b: any) => b.branchType === upgrade.type);
          if (branch && branch.upgrades) {
            allUpgrades = branch.upgrades;
          }
        }
        const maxLevel = Math.max(...allUpgrades.map(u => u.level), 1);
        const isMaxLevel = upgrade.level === maxLevel && upgrade.isUnlocked;
        let highlightClass = '';
        if (isBuyable) highlightClass = ' highlight-buyable';
        else if (isMaxLevel) highlightClass = ' highlight-maxlevel';
        // Grey out if another upgrade is queued and this is not the queued one
        const greyedOut = anyQueued && !isQueued;

        // Determine icon based on upgrade type
        let iconSrc: string | undefined;
        if (upgrade.type === UpgradeType.Mining) iconSrc = miningIcon;
        else if (upgrade.type === UpgradeType.Engines) iconSrc = enginesIcon;
        else if (upgrade.type === UpgradeType.Cargo) iconSrc = cargoIcon;

        // Type-based classes
        const typeClassName = upgrade.type === UpgradeType.Mining
          ? 'upgrade-type-mining'
          : upgrade.type === UpgradeType.Engines
            ? 'upgrade-type-engines'
            : upgrade.type === UpgradeType.Cargo
              ? 'upgrade-type-cargo'
              : '';
              
        return (
          <div
            key={upgrade.id}
            className={`upgrade-symbol-container ${typeClassName}${upgrade.isUnlocked ? ' unlocked' : ''}${cannotAfford ? ' cannot-afford' : ''}${isQueued ? ' queued' : ''}${highlightClass}`}
            onClick={() => {
              if (isBuyable && onUpgradeClick) onUpgradeClick(upgrade);
            }}
            title={upgrade.description}
            style={{ opacity: (disabled || cannotAfford || greyedOut) ? 0.6 : 1, pointerEvents: (disabled || cannotAfford || greyedOut) ? 'none' : 'auto', position: 'relative' }}
          >
            {/* Info button for this upgrade */}            
            <button
              className="upgrade-info-btn"
              type="button"
              tabIndex={-1}
              onClick={e => {
                e.stopPropagation();
                debugLog("Opening popover for upgrade:", upgrade);
                setPopoverOpenUpgrade(upgrade);
                setPopoverAnchor(e.currentTarget);
              }}
              aria-label="Upgrade info"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                background: 'var(--color-bg-secondary, #1a2638)',
                border: '1px solid var(--color-border-light, #334466)',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'translate(25%, 5%)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.10)',
                cursor: 'pointer',
                zIndex: 3,
                padding: 0,
                outline: 'none',
                opacity: (anyQueued || allCannotAfford) ? 0.6 : 1,
                pointerEvents: 'auto',
              }}
            >
              <span style={{ fontSize: '0.95em', color: '#2196f3', fontWeight: 'bold', lineHeight: 1 }}>i</span>
            </button>
            <div className="upgrade-bonus">
              {upgrade.type === UpgradeType.Mining ? 'Mining' : 
               upgrade.type === UpgradeType.Engines ? 'Engine' : 
               upgrade.type === UpgradeType.Cargo ? 'Cargo' : 
               upgrade.type}
            </div>
            <div className={`upgrade-symbol${isMaxLevel ? ' highlight-maxlevel' : ''}`} style={{ position: 'relative', width: 45, height: 45, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {iconSrc && <img src={iconSrc} alt="" style={{ width: 38, height: 38, objectFit: 'contain', zIndex: 1, transform: 'rotate(45deg)' }} />}
              <span className={`upgrade-roman${isMaxLevel ? ' highlight-maxlevel' : ''}`} style={{
                zIndex: 2,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: isMaxLevel ? '#ffb300' : 'var(--color-accent-blue, #2196f3)',
                textShadow: isMaxLevel ? '0 1px 2px #000, 0 0 4px #ffb300aa' : '0 1px 2px #000, 0 0 4px #2196f3aa',
                pointerEvents: 'none',
              }}>{toRoman(upgrade.level || 1)}</span>
            </div>
            <div className="upgrade-bonus">
              {isMaxLevel ? 'Max' : `+${formatNumber(upgrade.bonusValue * 100)}%`}
            </div>
            <div className="upgrade-cost">
              {isQueued ? (
                <span className="upgrade-queued-label" style={{position: 'static', background: 'none', color: 'var(--color-accent-blue, #2196f3)', fontWeight: 'bold', fontSize: '0.95rem', padding: 0}}>
                  Queued
                </span>
              ) : isMaxLevel ? (
                <span>-</span>
              ) : (
                formatNumber(upgrade.cost) + ' Ore'
              )}
            </div>
          </div>
        );
      })}      {/* Centralized popover using InfoPopover */}
      {popoverOpenUpgrade && popoverAnchor && (
        <InfoPopover
          placement="top" 
          anchorEl={popoverAnchor}
          title={(function() {
            // Map UpgradeType to full group name
            if (popoverOpenUpgrade.type === UpgradeType.Mining) return 'Mining Upgrades';
            if (popoverOpenUpgrade.type === UpgradeType.Engines) return 'Engines Upgrades';
            if (popoverOpenUpgrade.type === UpgradeType.Cargo) return 'Cargo Upgrades';
            return popoverOpenUpgrade.type ?? '?';
          })()}
          onClose={() => setPopoverOpenUpgrade(null)}
          minWidth={260}
          maxWidth={360}
        >
          <div style={{ minWidth: 240, maxWidth: 320 }}>
            {/* Description section */}
            <div style={{ marginBottom: '0.8rem', fontSize: '0.97em', opacity: 0.85 }}>
              {popoverOpenUpgrade.description || (
                // If no specific description, use a generic one based on upgrade type
                popoverOpenUpgrade.type === UpgradeType.Mining ? 
                  'Increases mining speed, allowing ships to extract more resources per second.' :
                popoverOpenUpgrade.type === UpgradeType.Engines ?
                  'Increases engine power, allowing ships to travel faster between mining zones.' :
                popoverOpenUpgrade.type === UpgradeType.Cargo ?
                  'Increases cargo capacity, allowing ships to carry more resources before needing to return to base.' :
                  'Enhances ship capabilities.'
              )}
            </div>
            
            {/* Specifications section header */}
            <div style={{ 
              fontSize: '0.97em', 
              fontWeight: 'bold', 
              borderBottom: '1px solid var(--color-border-light, #334466)', 
              paddingBottom: '4px', 
              marginBottom: '8px',
              color: (function() {
                // Color based on upgrade type
                if (popoverOpenUpgrade.type === UpgradeType.Mining) return '#ffcb4c'; // Gold
                if (popoverOpenUpgrade.type === UpgradeType.Engines) return '#4cffcb'; // Cyan
                if (popoverOpenUpgrade.type === UpgradeType.Cargo) return '#4ca6ff'; // Blue - changed from purple
                return 'var(--color-accent-blue, #2196f3)'; // Default blue
              })()
            }}>
              Upgrade Progression
            </div>
            
            {/* Enhanced table styling */}
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse', 
              fontSize: '0.97em', 
              border: '1px solid var(--color-border-light, #334466)', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <thead>
                <tr style={{ 
                  background: 'var(--color-bg-tertiary, #1e2a3e)', 
                  borderBottom: '1px solid var(--color-border-light, #334466)'
                }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 'bold' }}>Level</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>Bonus</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 'bold' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Show all levels for this upgrade type, highlight current
                  let allUpgrades: typeof props.upgrades = [];
                  if (props.upgradeTree && props.upgradeTree.branches) {
                    const branch = props.upgradeTree.branches.find((b: any) => b.branchType === popoverOpenUpgrade.type);
                    if (branch && branch.upgrades) {
                      allUpgrades = branch.upgrades;
                    }
                  }
                  if (allUpgrades.length === 0) {
                    allUpgrades = props.upgrades.filter(u => u.type === popoverOpenUpgrade.type);
                  }
                  const maxLevel = Math.max(...allUpgrades.map(u => u.level), 1);
                  const levels = Array.from({ length: maxLevel }, (_, i) => i + 1);
                  const upgradesByLevel: { [level: number]: typeof allUpgrades[0] | undefined } = {};
                  allUpgrades.forEach(u => { upgradesByLevel[u.level] = u; });
                  let runningBonus = 0;
                  const cumulativeBonuses: number[] = [0];
                  for (let level = 1; level <= maxLevel; level++) {
                    const upgrade = upgradesByLevel[level];
                    if (upgrade && typeof upgrade.bonusValue === 'number') {
                      runningBonus += upgrade.bonusValue;
                    }
                    cumulativeBonuses[level] = runningBonus;
                  }
                  // Use the correct base value for the upgrade type
                  let baseValue = 1;
                  if (popoverOpenUpgrade.type === UpgradeType.Mining) baseValue = props.baseMiningSpeed ?? 1;
                  else if (popoverOpenUpgrade.type === UpgradeType.Engines) baseValue = props.baseWarpSpeed ?? 1;
                  else if (popoverOpenUpgrade.type === UpgradeType.Cargo) baseValue = props.baseCargoCapacity ?? 1;
                  const currentLevel = popoverOpenUpgrade.level;
                  const getUpgradeUnit = (type: UpgradeType) => {
                    if (type === UpgradeType.Mining) return 'm³/s';
                    if (type === UpgradeType.Engines) return 'au/s';
                    if (type === UpgradeType.Cargo) return 'm³';
                    return '';
                  };
                  const unit = getUpgradeUnit(popoverOpenUpgrade.type);
                  const typeColor = (function() {
                    if (popoverOpenUpgrade.type === UpgradeType.Mining) return '#ffcb4c'; // Gold
                    if (popoverOpenUpgrade.type === UpgradeType.Engines) return '#4cffcb'; // Cyan
                    if (popoverOpenUpgrade.type === UpgradeType.Cargo) return '#4ca6ff'; // Blue - changed from purple
                    return '#2196f3';
                  })();
                  
                  return levels.map(level => {
                    const upgrade = upgradesByLevel[level];
                    const bonusText = upgrade ? `+${(upgrade.bonusValue * 100).toFixed(0)}%` : '-';
                    const total = baseValue * (1 + cumulativeBonuses[level]);
                    const isHighlighted = level === currentLevel;
                    // Calculate if this level is unlocked
                    const isUnlocked = !!upgrade?.isUnlocked;
                    
                    return (
                      <tr key={level} style={{ 
                        background: isHighlighted 
                          ? `rgba(${typeColor === '#ffcb4c' ? '255,203,76,0.1' : 
                              typeColor === '#4cffcb' ? '76,255,203,0.1' : 
                              typeColor === '#4ca6ff' ? '76,166,255,0.1' : 
                              '33,150,243,0.1'})` 
                          : 'none',
                        borderBottom: '1px solid var(--color-border-light, #334466)'
                      }}>
                        <td style={{ 
                          padding: '6px 8px',
                          fontWeight: isHighlighted ? 'bold' : 'normal'
                        }}>
                          {isHighlighted && (
                            <span style={{ 
                              marginRight: '5px', 
                              color: typeColor
                            }}>
                              ►
                            </span>
                          )}
                          {toRoman(level)}
                        </td>
                        <td style={{ 
                          textAlign: 'right', 
                          padding: '6px 8px', 
                          color: isHighlighted ? typeColor : 'inherit',
                          fontWeight: isHighlighted ? 'bold' : 'normal'
                        }}>{bonusText}</td>
                        <td style={{ 
                          textAlign: 'right', 
                          padding: '6px 8px',
                          color: isHighlighted ? typeColor : (isUnlocked ? 'var(--color-text-bright, #fff)' : 'var(--color-text-muted, #888)'),
                          fontWeight: isHighlighted ? 'bold' : 'normal'
                        }}>{total.toFixed(1)} {unit}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
            
            {/* Cost information section */}
            {!popoverOpenUpgrade.isUnlocked && (
              <div style={{ 
                marginTop: '12px', 
                padding: '8px', 
                borderRadius: '4px', 
                background: 'rgba(33,150,243,0.1)', 
                fontSize: '0.9em',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>Upgrade Cost:</span>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: playerOre !== undefined && popoverOpenUpgrade.cost > playerOre 
                    ? 'var(--color-text-error, #e41f01)' 
                    : 'var(--color-accent-blue, #2196f3)'
                }}>
                  {formatNumber(popoverOpenUpgrade.cost)} Ore
                </span>
              </div>
            )}
            
            {/* Max level note */}
            {popoverOpenUpgrade.isUnlocked && (() => {
              let allUpgrades = props.upgrades.filter(u => u.type === popoverOpenUpgrade.type);
              if (props.upgradeTree && props.upgradeTree.branches) {
                const branch = props.upgradeTree.branches.find((b: any) => b.branchType === popoverOpenUpgrade.type);
                if (branch && branch.upgrades) {
                  allUpgrades = branch.upgrades;
                }
              }
              const maxLevel = Math.max(...allUpgrades.map(u => u.level), 1);
              return popoverOpenUpgrade.level === maxLevel ? (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px', 
                  borderRadius: '4px', 
                  background: 'rgba(255,179,0,0.1)', 
                  fontSize: '0.9em',
                  color: '#ffb300',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ marginRight: '6px' }}>★</span>
                  <span>Maximum level reached</span>
                </div>
              ) : null;
            })()}
          </div>
        </InfoPopover>
      )}
    </div>
  );
};

export default ShipUpgradeDisplay;
